import { useEffect, useState } from 'react';
import { formatPrice } from '@cracked-mug/shared';
import { cn } from '../../lib/cn';

interface TipSelectorProps {
  subtotalCents: number;
  tipCents: number;
  onChange: (cents: number) => void;
}

interface Preset {
  id: string;
  label: string;
  toCents: (subtotal: number) => number;
}

const PRESETS: Preset[] = [
  { id: 'zero', label: '$0', toCents: () => 0 },
  { id: 'one', label: '$1', toCents: () => 100 },
  { id: 'two', label: '$2', toCents: () => 200 },
  { id: 'fifteen', label: '15%', toCents: (s) => Math.round(s * 0.15) },
  { id: 'twenty', label: '20%', toCents: (s) => Math.round(s * 0.2) },
];

export function TipSelector({ subtotalCents, tipCents, onChange }: TipSelectorProps) {
  const [showCustom, setShowCustom] = useState(false);
  const [customInput, setCustomInput] = useState('');

  // If the parent flips tipCents back to a preset value (e.g. on reset), hide
  // the custom field.
  useEffect(() => {
    if (!showCustom) return;
    const matched = PRESETS.find((p) => p.toCents(subtotalCents) === tipCents);
    if (matched) setShowCustom(false);
  }, [tipCents, subtotalCents, showCustom]);

  const handlePreset = (preset: Preset): void => {
    setShowCustom(false);
    setCustomInput('');
    onChange(preset.toCents(subtotalCents));
  };

  const handleCustomInput = (raw: string): void => {
    setCustomInput(raw);
    const dollars = parseFloat(raw);
    onChange(Number.isFinite(dollars) && dollars >= 0 ? Math.round(dollars * 100) : 0);
  };

  return (
    <div className="space-y-3">
      <p className="font-medium text-sm">Tip</p>
      <div className="flex flex-wrap gap-2">
        {PRESETS.map((preset) => {
          const presetCents = preset.toCents(subtotalCents);
          const selected = !showCustom && tipCents === presetCents;
          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => handlePreset(preset)}
              className={cn(
                'rounded-full border px-4 py-2 text-sm font-medium transition-colors min-h-[40px] inline-flex items-center gap-1.5',
                selected
                  ? 'bg-wca-red text-white border-wca-red'
                  : 'bg-wca-surface text-wca-black border-wca-gray-light hover:border-wca-red',
              )}
              aria-pressed={selected}
            >
              {preset.label}
              {presetCents > 0 && presetCents !== 100 && presetCents !== 200 && (
                <span className="font-mono text-xs opacity-80">({formatPrice(presetCents)})</span>
              )}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setShowCustom(true)}
          className={cn(
            'rounded-full border px-4 py-2 text-sm font-medium transition-colors min-h-[40px]',
            showCustom
              ? 'bg-wca-red text-white border-wca-red'
              : 'bg-wca-surface text-wca-black border-wca-gray-light hover:border-wca-red',
          )}
          aria-pressed={showCustom}
        >
          Custom
        </button>
      </div>
      {showCustom && (
        <div className="flex items-center gap-2">
          <label htmlFor="custom-tip" className="font-mono text-sm text-wca-gray-mid">
            $
          </label>
          <input
            id="custom-tip"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.25"
            value={customInput}
            onChange={(e) => handleCustomInput(e.target.value)}
            placeholder="0.00"
            className="w-24 rounded-lg bg-wca-gray-light p-2 focus:outline-none focus:ring-2 focus:ring-wca-red text-sm font-mono"
          />
        </div>
      )}
    </div>
  );
}
